import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DeleteHook from "../../../Hooks/DeleteHook";
import PostHooks from "../../../Hooks/PostHooks";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import { useQuery } from "../../../Hooks/useApi";

const SuperAdminPlans = () => {
  const [facilities, setFacilities] = useState([""]);
  const [refetch, setRefetch] = useState(false);
  const { data: plansResponse } = useQuery("/plan/getplan");
  const plans = plansResponse?.data || [];
  const [formData, setFormData] = useState({
    title: "",
    price: 0,
    day: 0,
  });

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (formData?._id) {
        await UpdateHooks(
          `https://servercareseebd.careseebd.com/api/v1/plan/updatePlan/${formData?._id}`,
          { ...formData, facilities }
        );
        toast.success("Plan updated successfully!");
        setFormData({ title: "", price: 0, day: 0 });
        setFacilities([""]);
        setRefetch(!refetch);
      } else {
        await PostHooks(
          `https://servercareseebd.careseebd.com/api/v1/plan/addPlan`,
          { ...formData, facilities },
          "Plan posted successfully!"
        );
        setFormData({
          title: "",
          price: 0,
          day: 0,
        });
        setFacilities([""]);
        setTimeout(() => {
          setRefetch(!refetch);
        }, 1000);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFacilities = (index, e) => {
    const newFacilities = [...facilities];
    newFacilities[index] = e.target.value;
    setFacilities(newFacilities);
  };

  const addInputField = () => {
    setFacilities([...facilities, ""]);
  };

  const removeInputField = (index) => {
    if (facilities.length < 2) {
      return toast.warning("You must have at least one facility listed.");
    }
    const newFacilities = [...facilities];
    newFacilities.splice(index, 1);
    setFacilities(newFacilities);
  };

  return (
    <div className="max-w-6xl mx-auto select-none space-y-6">
      {/* Header bar */}
      <div className="flex items-center gap-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
          <Icon icon="solar:bill-list-bold" className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight">Subscription Plans</h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">Configure membership plans, pricing tiers, and active days</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form panel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4 h-fit">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">
            {formData?._id ? "Edit Plan" : "Add Plan"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Plan Title</label>
              <input
                type="text"
                name="title"
                onChange={handleChange}
                value={formData.title}
                required
                placeholder="e.g. Basic Plan"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Duration (Days)</label>
                <input
                  type="number"
                  name="day"
                  onChange={handleChange}
                  value={formData.day}
                  required
                  placeholder="e.g. 30"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Price (৳)</label>
                <input
                  type="number"
                  name="price"
                  onChange={handleChange}
                  value={formData.price}
                  required
                  placeholder="e.g. 1000"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Facilities Included</label>
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {facilities.map((facility, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      required
                      type="text"
                      value={facility}
                      onChange={(e) => handleFacilities(index, e)}
                      placeholder="e.g. Unlimited consultations"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold text-slate-805"
                    />
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={addInputField}
                        className="p-2 bg-indigo-55 hover:bg-indigo-100 text-indigo-700 rounded-xl cursor-pointer transition-all border border-indigo-100"
                        title="Add Facility Row"
                      >
                        <Icon icon="solar:plus-outline" className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeInputField(index)}
                        className="p-2 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-xl cursor-pointer transition-all border border-rose-100"
                        title="Remove Facility Row"
                      >
                        <Icon icon="solar:trash-bin-trash-outline" className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              {formData?._id && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ title: "", price: 0, day: 0 });
                    setFacilities([""]);
                  }}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-500 font-extrabold text-[11px] transition-all hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="flex-1 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-[11px] rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              >
                {formData?._id ? "Update Plan" : "Add Plan"}
              </button>
            </div>
          </form>
        </div>

        {/* List panel */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">Active Subscription Plans</h2>
          <div className="overflow-x-auto border border-slate-100 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Plan Title</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {plans.length > 0 ? (
                  plans.map((plan) => (
                    <tr key={plan?._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 text-xs font-bold text-slate-800">
                        {plan?.title}
                      </td>
                      <td className="px-6 py-4 text-xs font-semibold text-slate-500">
                        {plan?.day} Days
                      </td>
                      <td className="px-6 py-4 text-xs font-black text-indigo-650">
                        ৳ {plan?.price}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div className="flex justify-end items-center gap-2">
                          <button
                            onClick={async () => {
                              setFormData(plan);
                              setFacilities(plan?.facilities || [""]);
                              scrollToTop();
                            }}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-150 rounded-lg transition-all cursor-pointer"
                            title="Edit Plan"
                          >
                            <Icon icon="basil:edit-outline" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              DeleteHook({
                                setRefetch,
                                refetch,
                                url: `https://servercareseebd.careseebd.com/api/v1/plan/deletePlan/${plan?._id}`,
                              })
                            }
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 hover:border-rose-150 rounded-lg transition-all cursor-pointer"
                            title="Delete Plan"
                          >
                            <Icon icon="material-symbols:delete-outline" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-10 text-xs text-slate-400">
                      No plans added.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPlans;
